import { useQueryClient } from '@tanstack/react-query';
import useMutationApi from '../useMutationApi';
import { RebateAgreementDTO } from './types';

interface CreateRebateAgreementRequest {
  supplierId: string;
  files?: File[]; // Optional for manual creation
  startDate: string;
  endDate: string;
  model?: string;
  agreementName?: string; // Optional agreement name for manual creation
  manualMode?: boolean; // Explicitly indicate manual creation mode
  replaceExistingAgreementId?: string; // If provided, delete this agreement before creating new one
}

const useCreateRebateAgreement = () => {
  const queryClient = useQueryClient();

  return useMutationApi<CreateRebateAgreementRequest, RebateAgreementDTO>({
    endpoint: 'rebates/agreements',
    method: 'POST',
    transformRequest: (body: CreateRebateAgreementRequest) => {
      const formData = new FormData();
      formData.append('supplierId', body.supplierId);
      // Append multiple files if provided (for AI extraction)
      if (body.files && body.files.length > 0) {
        body.files.forEach((file) => {
          formData.append('files', file);
        });
      }
      formData.append('startDate', body.startDate);
      formData.append('endDate', body.endDate);
      if (body.model) {
        formData.append('model', body.model);
      }
      if (body.agreementName) {
        formData.append('agreementName', body.agreementName);
      }
      if (body.manualMode !== undefined) {
        formData.append('manualMode', body.manualMode.toString());
      }
      if (body.replaceExistingAgreementId) {
        formData.append(
          'replaceExistingAgreementId',
          body.replaceExistingAgreementId,
        );
      }
      return formData;
    },
    options: {
      onSuccess: () => {
        // Invalidate rebate agreements queries to refresh the data
        queryClient.invalidateQueries({ queryKey: ['rebateAgreements'] });
      },
    },
    successMessage: 'Rebate agreement uploaded successfully',
  });
};

export default useCreateRebateAgreement;
